/**
 * Step 6 — Preview manifest.
 *
 * Renders the canonical-JSON shape of what will be signed. We
 * deliberately use the same canonical serializer the mock engine
 * uses so "what you see is what you sign." The signature itself is
 * absent at this stage (it happens on step 7).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import type { BehavioralContract, DomainType, Manifest } from '@/api/types';

import {
  parametersFromDraft,
  portsFromDraft,
  useSparkStore,
} from '../store';
import { WizardFooter } from './Step1Basic';
import { canonicalJsonPreview } from '../components/ManifestPreview';

export function Step6Preview({
  onAdvance,
  onBack,
}: {
  onAdvance: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('spark');
  const state = useSparkStore();

  const { contractPreview, manifestPreview } = useMemo(() => {
    const contract: BehavioralContract = {
      inputs: portsFromDraft(state.input_ports),
      outputs: portsFromDraft(state.output_ports),
      parameters: parametersFromDraft(state.parameters),
      behavior_rules: [],
      domain_adapter: state.domain as DomainType,
      macros: [],
    };
    const manifestSkeleton: Omit<Manifest, 'root_id' | 'contract_hash' | 'wasm_hash' | 'source_hash' | 'created_at'> & {
      root_id: string;
      contract_hash: string;
      wasm_hash: string;
      source_hash: string;
      created_at: { iso: string; source: 'Monotonic' };
    } = {
      gi_version: '0.1.0',
      root_id: '(assigned on Spark)',
      version: state.version,
      name: state.name,
      description: state.description,
      domain: state.domain as DomainType,
      author: {
        name: state.author_name,
        public_key: '(derived from your Vault keypair on Spark)' as Manifest['author']['public_key'],
      },
      contract_hash: '(derived from contract on Spark)',
      source_hash: '(placeholder — replace with signed source)',
      wasm_hash: state.wasm_hash ?? '(placeholder — upload a .wasm on step 5 for real bytes)',
      compiler: { name: 'mock-compiler', version: '0.1.0', target: 'wasm32-unknown' },
      lineage: { root_id: '(assigned on Spark)', depth: 0 },
      created_at: { iso: '(assigned on Spark)', source: 'Monotonic' },
    };
    return {
      contractPreview: canonicalJsonPreview(contract),
      manifestPreview: canonicalJsonPreview(manifestSkeleton),
    };
  }, [state]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onAdvance();
      }}
      className="flex flex-col gap-5"
    >
      {state.coach_filled ? (
        <div className="flex items-start gap-3 rounded-md border border-spark/30 bg-spark-soft/40 p-3 text-sm text-ink">
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-spark text-[10px] font-semibold text-white"
          >
            GI
          </span>
          <div className="flex-1">
            <p className="font-medium">{t('steps.6.coach_banner_title')}</p>
            <p className="mt-0.5 text-xs text-ink-muted">
              {t('steps.6.coach_banner_body')}
            </p>
          </div>
        </div>
      ) : null}

      <p className="text-sm text-ink-muted">{t('steps.6.intro')}</p>

      <Card>
        <CardTitle>{t('steps.6.manifest_title')}</CardTitle>
        <CardSubtitle>{t('steps.6.manifest_sub')}</CardSubtitle>
        <pre className="mt-3 max-h-[320px] overflow-auto rounded-md border border-border bg-canvas p-4 font-mono text-xs leading-relaxed text-ink">
          {manifestPreview}
        </pre>
      </Card>

      <Card>
        <CardTitle>{t('steps.6.contract_title')}</CardTitle>
        <CardSubtitle>{t('steps.6.contract_sub')}</CardSubtitle>
        <pre className="mt-3 max-h-[320px] overflow-auto rounded-md border border-border bg-canvas p-4 font-mono text-xs leading-relaxed text-ink">
          {contractPreview}
        </pre>
      </Card>

      <WizardFooter onBack={onBack}>
        <Button type="submit" variant="primary">
          {t('steps.6.cta')}
        </Button>
      </WizardFooter>
    </form>
  );
}
